/**
 * Composer · B2B mode — the 80% path.
 *
 * Single field (GSTIN) → Fetch button → green verified panel below
 * showing the four fields GSTN returned + optional override
 * disclosure for display name / contact / phone / email.
 *
 * State is owned by the parent (CustomerComposer in the route).
 * This pane just renders + emits change events; the parent decides
 * whether the Save button in the sticky footer is enabled and what
 * to do with the result.
 *
 * The Fetch action calls `trpc.onboarding.lookupGstin`. On error,
 * we keep the input + show the message inline — the user can edit
 * and retry without losing context.
 */
import { useState } from "react";
import { Hash, Search } from "lucide-react";
import { trpc } from "../../../lib/trpc.ts";
import { Button, Input, cn } from "@flint/ui";
import {
  GstinVerifiedPanel,
  type CustomerOverrides,
  type GstinLookupResult,
} from "../components/gstin-verified-panel.tsx";

export interface ComposerB2BState {
  gstin: string;
  /** Set after a successful lookup; null while we haven't fetched yet. */
  lookup: GstinLookupResult | null;
  overrides: CustomerOverrides;
}

export interface ComposerB2BProps {
  state: ComposerB2BState;
  onChange: (patch: Partial<ComposerB2BState>) => void;
}

export function ComposerB2B({ state, onChange }: ComposerB2BProps) {
  // `onboarding.lookupGstin` is a query on the server. The composer has an
  // explicit Fetch button (no auto-fire), so we call it imperatively via
  // `utils.fetch()` rather than wiring `useQuery({ enabled })` to a manual
  // trigger. Local pending/error mirror what `useMutation` exposed before.
  const utils = trpc.useUtils();
  const [isPending, setIsPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleFetch = async () => {
    const gstin = state.gstin.trim().toUpperCase();
    if (gstin.length !== 15) return;
    setIsPending(true);
    setErrorMessage(null);
    try {
      const result = await utils.onboarding.lookupGstin.fetch({ gstin });
      onChange({ lookup: result });
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "GSTIN lookup failed");
    } finally {
      setIsPending(false);
    }
  };

  return (
    <section
      aria-label="Indian business customer"
      className="rounded-md border border-line bg-paper shadow-card"
    >
      <header className="px-[18px] pt-3.5 pb-2 border-b border-line">
        <h3 className="text-sm font-semibold text-ink m-0">Paste GSTIN</h3>
        <p className="text-xs text-muted mt-0.5 leading-snug">
          15-character GSTIN. Flint fetches legal name, address and tax treatment from the GSTN
          portal — usually under 2 seconds.
        </p>
      </header>

      <div className="px-[18px] py-[18px] flex flex-col gap-2">
        <div className="flex items-stretch gap-2">
          <div className="relative flex-1">
            <Hash
              aria-hidden
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none"
              strokeWidth={2}
            />
            <Input
              type="text"
              value={state.gstin}
              onChange={(e) => onChange({ gstin: e.target.value.toUpperCase(), lookup: null })}
              placeholder="27AABCS1234N1Z5"
              maxLength={15}
              spellCheck={false}
              autoComplete="off"
              aria-label="Customer GSTIN"
              aria-describedby="gstin-hint"
              className="pl-9 font-mono tracking-wider uppercase placeholder:tracking-wider"
            />
          </div>
          <Button
            type="button"
            intent="primary"
            size="lg"
            onClick={handleFetch}
            disabled={state.gstin.trim().length !== 15 || isPending}
          >
            <Search className="w-3.5 h-3.5" strokeWidth={2.4} />
            {isPending ? "Fetching…" : "Fetch"}
          </Button>
        </div>

        <p
          id="gstin-hint"
          className={cn(
            "text-xs leading-snug",
            errorMessage ? "text-bad font-medium" : "text-muted",
          )}
          role={errorMessage ? "alert" : undefined}
        >
          {errorMessage ?? hintFor(state.gstin)}
        </p>

        {state.lookup ? (
          <GstinVerifiedPanel
            result={state.lookup}
            overrides={state.overrides}
            onOverridesChange={(patch) => onChange({ overrides: { ...state.overrides, ...patch } })}
            className="mt-1"
          />
        ) : null}
      </div>
    </section>
  );
}

/**
 * Hint copy that adapts to what's currently in the GSTIN field — turns
 * the empty placeholder line into a progress cue as the user types.
 * Kept tiny on purpose: the section header already explains *what*
 * GSTIN is; this line tells the user *where they are* in the entry.
 */
function hintFor(gstin: string): string {
  const length = gstin.trim().length;
  if (length === 0) return "Paste from a purchase order or invoice — case doesn’t matter.";
  if (length < 15) return `${length} of 15 characters`;
  if (length === 15)
    return "Looks good — click Fetch to pull legal name, address and tax treatment from GSTN.";
  return "GSTIN is exactly 15 characters.";
}
